import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { SectionCard, Skeleton } from "@/components/Primitives";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Attachments from "@/components/Attachments";
import { useQuickAdd } from "@/context/QuickAddContext";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, StickyNote, CheckSquare, CalendarPlus, Trophy, XCircle,
  TrendingUp, MapPin, CalendarClock, Clock, User, Building2, Target,
} from "lucide-react";

const TL = {
  call: { icon: Phone, c: "#00C9E0" }, email: { icon: Mail, c: "#38BDF8" },
  meeting: { icon: CalendarPlus, c: "#A78BFA" }, visit: { icon: MapPin, c: "#9ACC32" },
  note: { icon: StickyNote, c: "#F59E0B" }, task: { icon: CheckSquare, c: "#00C9E0" },
  contact: { icon: Phone, c: "#00C9E0" }, opportunity: { icon: TrendingUp, c: "#FE3967" },
  contract: { icon: TrendingUp, c: "#5B880C" }, status_change: { icon: Clock, c: "#64748B" },
};

const daysInStage = (o) => {
  const d = o.updated_at || o.created_at;
  return d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)) : null;
};

function Timeline({ items }) {
  if (!items.length) return <p className="text-sm text-slate-400 py-8 text-center">No activity yet.</p>;
  return (
    <ol className="relative ml-3 space-y-5 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
      {items.map((t) => {
        const m = TL[t.type] || TL.note; const Ic = m.icon;
        return (
          <li key={t.id} className="relative ml-7" data-testid={`opp-tl-${t.id}`}>
            <span className="absolute -left-[34px] flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-white" style={{ background: `${m.c}22` }}>
              <Ic className="h-3.5 w-3.5" style={{ color: m.c }} />
            </span>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-slate-800">{t.title}</p>
              <span className="text-xs text-slate-400 whitespace-nowrap tnum">{fmtDateTime(t.created_at)}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">by {t.created_by_name}{t.meta?.notes ? ` · ${t.meta.notes}` : ""}</p>
          </li>
        );
      })}
    </ol>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-slate-700 mt-0.5">{value || "—"}</p>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openQuickAdd } = useQuickAdd();
  const [opp, setOpp] = useState(null);
  const [biz, setBiz] = useState(null);
  const [contact, setContact] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stages, setStages] = useState([]);

  const load = useCallback(async () => {
    try {
      const o = await api.get(`/opportunities/${id}`);
      setOpp(o.data);
      const bId = o.data.business_id;
      const [st, biz, tl, tk, ct] = await Promise.all([
        api.get("/pipeline_stages"),
        bId ? api.get(`/businesses/${bId}`).catch(() => null) : Promise.resolve(null),
        bId ? api.get(`/businesses/${bId}/timeline`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
        bId ? api.get(`/tasks?business_id=${bId}`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
        o.data.contact_id ? api.get(`/contacts/${o.data.contact_id}`).catch(() => null) : Promise.resolve(null),
      ]);
      setStages(st.data.items.sort((a, b) => a.order - b.order));
      setBiz(biz?.data || null);
      setTimeline(tl.data.items);
      setTasks(tk.data.items);
      setContact(ct?.data || null);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); navigate("/pipeline"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("quickadd:saved", h);
    return () => window.removeEventListener("quickadd:saved", h);
  }, [load]);

  const changeStage = async (stageName) => {
    const stage = stages.find((s) => s.name === stageName);
    try {
      await api.put(`/opportunities/${id}`, { stage: stageName, probability: stage?.probability });
      toast.success(`Moved to ${stageName}`); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const markWon = async () => {
    try { await api.post(`/opportunities/${id}/win`); toast.success("Deal won! Advertiser activated."); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const markLost = async () => {
    try { await api.put(`/opportunities/${id}`, { status: "Lost", stage: "Lost" }); toast.success("Marked as lost"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const ctx = () => ({ prefill: { business_id: opp.business_id, business_name: opp.business_name }, onSaved: load });

  if (!opp) return <div className="space-y-4"><Skeleton className="h-28" /><Skeleton className="h-64" /></div>;

  const phone = biz?.phone, email = biz?.email || contact?.email;
  const dis = daysInStage(opp);
  const closed = ["Won", "Lost"].includes(opp.status);

  return (
    <div data-testid="page-opportunity-detail">
      <button onClick={() => navigate("/pipeline")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" />Pipeline</button>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm cc-fade-up">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#00C9E0] via-[#9ACC32] to-[#FE3967]" />
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-[#1E2025]">{opp.name || opp.business_name}</h1>
              <StatusChip value={opp.status || "Open"} />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00C9E0]/12 text-[#0094A8] px-2.5 py-1 text-xs font-semibold">{opp.stage}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-slate-500">
              {opp.business_name && <button onClick={() => opp.business_id && navigate(`/businesses/${opp.business_id}`)} className="flex items-center gap-1.5 hover:text-[#0094A8]"><Building2 className="h-4 w-4" />{opp.business_name}</button>}
              {contact && <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{contact.first_name} {contact.last_name}</span>}
              {(opp.zone || opp.territory) && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{opp.zone || opp.territory}</span>}
              {opp.expected_close && <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" />{fmtDate(opp.expected_close)}</span>}
              {dis != null && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{dis}d in stage</span>}
            </div>
            <p className="mt-3 font-display text-2xl font-bold text-[#0094A8] tnum">{fmtMoney(opp.expected_value)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {phone && <Button size="sm" asChild data-testid="opp-call"><a href={`tel:${phone}`}><Phone className="h-4 w-4" />Call</a></Button>}
            {email && <Button size="sm" variant="outline" asChild data-testid="opp-email"><a href={`mailto:${email}`}><Mail className="h-4 w-4" />Email</a></Button>}
            <Button size="sm" variant="outline" onClick={() => openQuickAdd("task", ctx())} data-testid="opp-add-task"><CheckSquare className="h-4 w-4" />Task</Button>
            <Button size="sm" variant="outline" onClick={() => openQuickAdd("meeting", ctx())} data-testid="opp-add-meeting"><CalendarPlus className="h-4 w-4" />Meeting</Button>
          </div>
        </div>

        {!closed && (
          <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Stage</span>
            <Select value={opp.stage} onValueChange={changeStage}>
              <SelectTrigger className="w-48 h-8" data-testid="opp-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button size="sm" className="bg-[#9ACC32] hover:bg-[#5B880C] text-white" onClick={markWon} data-testid="opp-win"><Trophy className="h-4 w-4" />Mark Won</Button>
              <Button size="sm" variant="outline" className="text-[#C42607]" onClick={markLost} data-testid="opp-lost"><XCircle className="h-4 w-4" />Mark Lost</Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="opp-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="activity" data-testid="opp-tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="opp-tab-tasks">Tasks ({tasks.length})</TabsTrigger>
            <TabsTrigger value="files" data-testid="opp-tab-files">Files</TabsTrigger>
            <TabsTrigger value="notes" data-testid="opp-tab-notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <SectionCard title="Opportunity Details">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                <Field label="Value" value={fmtMoney(opp.expected_value)} />
                <Field label="Product" value={opp.product} />
                <Field label="Stage" value={opp.stage} />
                <Field label="Territory / Zone" value={[opp.territory, opp.zone].filter(Boolean).join(" · ")} />
                <Field label="Expected Close" value={opp.expected_close ? fmtDate(opp.expected_close) : null} />
                <Field label="Salesperson" value={opp.assigned_to_name} />
                <Field label="Lead Source" value={opp.lead_source} />
                <Field label="Primary Contact" value={contact ? `${contact.first_name} ${contact.last_name || ""}`.trim() : null} />
                <Field label="Days in Stage" value={dis != null ? `${dis} days` : null} />
              </div>
              {opp.notes && <div className="mt-5 pt-4 border-t border-slate-100"><Field label="Notes" value={opp.notes} /></div>}
            </SectionCard>
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <SectionCard title="Activity Timeline"><Timeline items={timeline} /></SectionCard>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <SectionCard title="Tasks" action={<Button size="sm" variant="outline" onClick={() => openQuickAdd("task", ctx())}>Add Task</Button>}>
              {tasks.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No tasks.</p> : (
                <div className="divide-y divide-slate-100">
                  {tasks.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-3" data-testid={`opp-task-${t.id}`}>
                      <div><p className="font-semibold text-sm">{t.title}</p><p className="text-xs text-slate-400">Due {fmtDate(t.due_date)}</p></div>
                      <StatusChip value={t.status} />
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </TabsContent>

          <TabsContent value="files" className="mt-4">
            <Attachments relatedType="opportunities" relatedId={id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <SectionCard title="Notes">
              {opp.business_id ? (
                <Timeline items={timeline.filter((t) => t.type === "note")} />
              ) : (
                <p className="text-sm text-slate-400 py-6 text-center">Link a business to capture notes on its timeline.</p>
              )}
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
